import React from 'react';
import I18n from 'i18n-js';

import { core_apimessages_ContentSponsorship_SponsorshipType } from '../../rql';

import CoreLink from '../../components/core_link';
import { MParticleEventName } from '../../elog/mparticle_types';
import SearchHeader from '../headers/search_header';
import { useUser } from '../../user_hooks';
import { IUser } from '../../components/user_context_provider';
import { trackEvent } from '../../elog/mparticle_tracker';
import AdTag from '../../components/ads/ad_tag';
import { imageUploadURL } from '../../url_helpers';
import HomepageCategorySearch from '../../components/homepage_category_search';
import { shouldDisplayNewHomepageSearch } from '../../components/homepage/homepage_category_search_helpers';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

// This component can be rendered with props from either:
// - a CMS component (cms-ui/src/components/site_banner_hero_builder.tsx)
// - a graphql query (discovery-ui/src/components/discovery/site_banner_hero_container.tsx)

const COMPONENT_NAME = 'SiteBannerHero';
export const DEFAULT_SEARCH_HEADER = 'SearchHeader';

interface SiteBannerProps {
  id?: string;
  heading?: string;
  subheading?: string;
  callToAction?: string;
  url?: string;
  hasPhotos?: boolean;
  imageUrl?: string;
  cmsBannerImage?: string;
  sponsoredContentType?: core_apimessages_ContentSponsorship_SponsorshipType;
  sponsoredContentPartner?: string;
}

interface ContentSponsorshipProps {
  contentSponsorship: {
    sponsorshipType?: core_apimessages_ContentSponsorship_SponsorshipType;
    partnershipName?: string;
  };
}

interface UserProps {
  user: Partial<IUser>;
}

function buildClickEvent(siteBanner: SiteBannerProps) {
  return {
    eventName: MParticleEventName.SiteBanner,
    componentName: COMPONENT_NAME,
    entityId: siteBanner.heading,
    isAd: !!siteBanner.sponsoredContentType,
    isDefault: false,
  };
}

function PartnershipSponsorshipContent({ contentSponsorship }: ContentSponsorshipProps) {
  if (
    contentSponsorship?.sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.PARTNERSHIP &&
      contentSponsorship?.partnershipName) {
    return (
      <p className="size-80 mb-1 style-italic">
        {I18n.t('commons.contentSponsorship.partnership', { partner: contentSponsorship.partnershipName })}
      </p>
    );
  }

  return null;
}

function AdSponsorshipContent({ contentSponsorship }: ContentSponsorshipProps) {
  if (contentSponsorship?.sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.AD) {
    return (
      <div className="site-banner-hero__ad">
        <AdTag inline small />
      </div>
    );
  }

  return null;
}

function DefaultSearchHeader({ user }: UserProps) {
  const searchHeaderSubtitleText = user.loggedOut ? null : I18n.t('commons.searchHeader.loggedInSubtitle');

  return (
    <SearchHeader
      subtitle={searchHeaderSubtitleText}
      trackSearchFn={buildTrackSearchFn()}
      shouldTrackViewEvent
      renderSecondaryImage
      rotatingImages
    />
  );
}

function getBackgroundImageStyle(siteBanner: SiteBannerProps) {
  if (siteBanner.cmsBannerImage) {
    return { backgroundImage: `url(${imageUploadURL(siteBanner.cmsBannerImage)})` };
  }

  return { backgroundImage: `url(${siteBanner.imageUrl})` };
}

function buildTrackSearchFn() {
  return function trackSearchFn(query) {
    trackEvent(
      {
        eventName: MParticleEventName.SiteSearch,
        componentName: DEFAULT_SEARCH_HEADER,
        query,
        isDefault: true,
        interactionType: 'fromSearchHeader',
      },
    );
  };
}

export default function SiteBannerHero(siteBanner: SiteBannerProps) {
  const user = useUser();
  const renderSiteBanner = siteBanner?.hasPhotos || siteBanner?.cmsBannerImage;

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: COMPONENT_NAME,
    entityId: siteBanner?.id,
  }, !!renderSiteBanner);

  if (!renderSiteBanner) {
    if (shouldDisplayNewHomepageSearch(user)) return <HomepageCategorySearch />;

    return <DefaultSearchHeader user={user} />;
  }

  const contentSponsorship = {
    sponsorshipType: siteBanner.sponsoredContentType,
    partnershipName: siteBanner.sponsoredContentPartner,
  };

  return (
    <div className="site-banner-hero">
      <CoreLink
        to={siteBanner.url}
        className="site-banner-hero__inner"
        style={getBackgroundImageStyle(siteBanner)}
        clickEvent={buildClickEvent(siteBanner)}
      >
        <div className="site-banner-hero__content">
          <PartnershipSponsorshipContent contentSponsorship={contentSponsorship} />
          <p className="site-banner-hero__subtext">
            {siteBanner.subheading}
          </p>
          <h2 className="site-banner-hero__headline">
            {siteBanner.heading}
          </h2>
          <span className="site-banner-hero__cta">
            {siteBanner.callToAction}
          </span>
        </div>
        <AdSponsorshipContent contentSponsorship={contentSponsorship} />
      </CoreLink>
    </div>
  );
}
